import type { Observable } from 'rxjs'
import { of } from 'rxjs'
import type { Omit } from '../../../types/definition'
import type { Unit } from '../Unit'
import type { FieldsValue } from '../formBuilder'
import type { ValidatorObservableSubject } from './ValidatorResult'
import { ValidationResultType } from './ValidatorResult'

export namespace Validator {
    export interface options {
        warning?: boolean
        invalidMessage?: string
    }

    interface baseValidator {
        isAsync: boolean
    }

    export interface validatorsBuilded {
        [k: string]: Validator.validatorBuilded
    }

    export type validatorBuilded<
        value extends Unit.UnionFields['v'] = Unit.UnionFields['v'],
        data extends Unit.UnionFields['d'] = Unit.UnionFields['d'],
        fields extends FieldsValue = FieldsValue,
        fieldObserved extends keyof fields = keyof fields,
    > = validatorSingleBuilded<value, data> | validatorMultipleBuilded<fields, fieldObserved>

    export type singleFromValidatorObservableSubject = Omit<ValidatorObservableSubject, 'type'> & {
        type: Exclude<ValidatorObservableSubject['type'], ValidationResultType.UNKNOW>
    }

    export type pickFromValidatorObservableSubject<k extends ValidationResultType> = Omit<
        ValidatorObservableSubject,
        'type'
    > & { type: k }

    export type validatorSingle<value, data> = {
        validate: (value: value, data: data) => Observable<singleFromValidatorObservableSubject>
    } & baseValidator

    export interface validatorSingleBuilded<value, data> {
        type: 'single'
        validator: validatorSingle<value, data>
    }

    export type validatorMultiple<fields> = {
        validate: (fields: fields) => Observable<ValidatorObservableSubject>
    } & baseValidator

    export interface validatorMultipleBuilded<fields, fieldObserved> {
        type: 'multiple'
        fieldsObserved: Array<fieldObserved>
        showErrorOn: fieldObserved
        validator: validatorMultiple<fields>
    }
}

export const buildSingleValidator = <options extends Validator.options, value, data>(
    validator: (options: options) => Validator.validatorSingle<value, data>,
    options: options
): Validator.validatorSingleBuilded<value, data> => ({
    type: 'single',
    validator: validator(options),
})

export const buildMultipleValidator = <options extends Validator.options, fieldObserved extends keyof fields, fields>(
    validator: (options: options) => Validator.validatorMultiple<fields>,
    fieldsObserved: Array<fieldObserved>,
    fieldObserved: fieldObserved,
    options: options
): Validator.validatorMultipleBuilded<fields, fieldObserved> => ({
    type: 'multiple',
    fieldsObserved: fieldsObserved,
    showErrorOn: fieldObserved,
    validator: validator(options),
})

export const validateCompleteFail = (): Observable<
    Validator.pickFromValidatorObservableSubject<ValidationResultType.FAIL>
> => {
    return of({
        type: ValidationResultType.FAIL,
        message: 'Impossibile validare, riprova fra qualche instante',
    })
}

export const validateCompleteSuccess = (): Observable<
    Validator.pickFromValidatorObservableSubject<ValidationResultType.SUCCESS>
> => {
    return of({
        type: ValidationResultType.SUCCESS,
    })
}

export const validateCompleteUnknown = (): Observable<
    Validator.pickFromValidatorObservableSubject<ValidationResultType.UNKNOW>
> => {
    return of({
        type: ValidationResultType.UNKNOW,
    })
}

export const validateCompleteError = (
    options: Validator.options,
    defaultInvalidMessage: string
): Observable<
    Validator.pickFromValidatorObservableSubject<ValidationResultType.ERROR | ValidationResultType.WARNING>
> => {
    return of({
        type: options.warning ? ValidationResultType.WARNING : ValidationResultType.ERROR,
        message: options.invalidMessage ? options.invalidMessage : defaultInvalidMessage,
    })
}

export const validateCompleteWarning = (
    options: Validator.options,
    defaultInvalidMessage: string
): Observable<Validator.pickFromValidatorObservableSubject<ValidationResultType.WARNING>> => {
    return of({
        type: ValidationResultType.WARNING,
        message: options.invalidMessage ? options.invalidMessage : defaultInvalidMessage,
    })
}
